(function () {
  const SELECTOR = "[data-try-on-calendar]";

  function pad(value) {
    return String(value).padStart(2, "0");
  }

  function toDateString(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatLongDate(dateString) {
    const date = new Date(`${dateString}T00:00:00`);
    return date.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }

  function formatTime(timeString, options = {}) {
    const [hoursRaw, minutesRaw] = String(timeString || "00:00").split(":");
    const hours = Number(hoursRaw) || 0;
    const minutes = Number(minutesRaw) || 0;
    const suffix = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    const time = `${displayHours}:${String(minutes).padStart(2, "0")}`;

    return options.omitSuffix ? time : `${time}${suffix}`;
  }

  function formatTimeRange(startTime, endTime) {
    if (!startTime) return "";
    if (!endTime) return formatTime(startTime);

    const startSuffix = Number(String(startTime).split(":")[0] || 0) >= 12 ? "pm" : "am";
    const endSuffix = Number(String(endTime).split(":")[0] || 0) >= 12 ? "pm" : "am";

    return `${formatTime(startTime, { omitSuffix: startSuffix === endSuffix })} - ${formatTime(endTime)}`;
  }

  function money(value) {
    const amount = Number(value || 0);
    if (!amount) return "Free";

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "AUD",
    }).format(amount);
  }

  function buildUrl(root) {
    const endpoint = root.dataset.apiUrl || "/apps/dress-rental/try-ons";
    const url = new URL(endpoint, window.location.origin);

    url.searchParams.set("shop", root.dataset.shop || "");
    url.searchParams.set("productId", root.dataset.productId || "");
    url.searchParams.set("productGid", root.dataset.productGid || "");
    url.searchParams.set("productTitle", root.dataset.productTitle || "");

    return url.toString();
  }

  function getSelectedVariantId(root) {
    const urlVariantId = new URLSearchParams(window.location.search).get("variant");
    if (urlVariantId) return String(urlVariantId);

    const productFormVariantId =
      document.querySelector('form[action*="/cart/add"] [name="id"]')?.value ||
      document.querySelector('product-form [name="id"]')?.value;

    if (productFormVariantId) return String(productFormVariantId);

    return String(root.dataset.variantId || "");
  }

  function getSelectedVariantGid(root) {
    const variantId = getSelectedVariantId(root);
    return variantId ? `gid://shopify/ProductVariant/${variantId}` : String(root.dataset.variantGid || "");
  }

  async function addTryOnProductToCart(root, orderLineItem) {
    const variantId = String(orderLineItem?.variantId || getSelectedVariantId(root) || "");

    if (!variantId) {
      throw new Error("Please select a product option before continuing.");
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: orderLineItem?.properties || {},
          },
        ],
      }),
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result?.description || result?.message || "Your try-on was saved, but the product could not be added to checkout.");
    }

    window.location.href = "/checkout";
  }

  function monthDays(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    for (let blank = 0; blank < firstDay.getDay(); blank += 1) {
      days.push(null);
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      days.push(new Date(year, month, day));
    }

    return days;
  }

  function createState(data) {
    const availableDates = Array.isArray(data.availableDates) ? data.availableDates : [];
    const firstDate = availableDates[0] || toDateString(new Date());
    const firstDateObject = new Date(`${firstDate}T00:00:00`);

    return {
      data,
      selectedDate: firstDate,
      selectedTime: "",
      monthDate: new Date(firstDateObject.getFullYear(), firstDateObject.getMonth(), 1),
      availableDateSet: new Set(availableDates),
    };
  }

  function render(root, state) {
    const data = state.data;
    const heading = root.dataset.heading || "Book a try-on";
    const buttonLabel = root.dataset.buttonLabel || "Book try-on";
    const showLocation = root.dataset.showLocation !== "false";
    const showPrice = root.dataset.showFee !== "false";
    const showDuration = root.dataset.showDuration !== "false";
    const showProductBadge = root.dataset.showProductBadge !== "false";
    const locationOverride = root.dataset.locationOverride || "";
    const locationText = locationOverride || data.settings?.location;
    const productPrice = Number(data.product?.price || root.dataset.productPrice || 0);
    const selectedSlots = data.slotsByDate[state.selectedDate] || [];
    const monthLabel = state.monthDate.toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    });

    if (!state.selectedTime) {
      state.selectedTime = selectedSlots.find((slot) => slot.available)?.value || "";
    }

    root.innerHTML = `
      <div class="dr-try-on__card">
        <div class="dr-try-on__header">
          <div>
            <h2>${escapeHtml(heading)}</h2>
            <p>Choose a date and time to try on this item.</p>
          </div>
          ${showProductBadge && data.product?.title ? `<span class="dr-try-on__badge">${escapeHtml(data.product.title)}</span>` : ""}
        </div>

        <div class="dr-try-on__meta">
          ${showLocation && locationText ? `<span><strong>Location:</strong> ${escapeHtml(locationText)}</span>` : ""}
          ${showPrice ? `<span><strong>Price:</strong> ${escapeHtml(money(productPrice))}</span>` : ""}
          ${showDuration && data.settings?.appointmentDuration ? `<span><strong>Duration:</strong> ${escapeHtml(data.settings.appointmentDuration)} minutes</span>` : ""}
        </div>

        ${data.settings?.customerNotes ? `<p class="dr-try-on__note">${escapeHtml(data.settings.customerNotes)}</p>` : ""}

        <div class="dr-try-on__calendar-head">
          <button type="button" data-try-on-prev aria-label="Previous month">&#8249;</button>
          <strong>${escapeHtml(monthLabel)}</strong>
          <button type="button" data-try-on-next aria-label="Next month">&#8250;</button>
        </div>

        <div class="dr-try-on__weekdays">
          ${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => `<span>${day}</span>`).join("")}
        </div>

        <div class="dr-try-on__calendar">
          ${monthDays(state.monthDate).map((date) => {
            if (!date) return '<span class="dr-try-on__blank"></span>';

            const value = toDateString(date);
            const available = state.availableDateSet.has(value);
            const selected = value === state.selectedDate;

            return `
              <button
                type="button"
                class="dr-try-on__day ${selected ? "is-selected" : ""} ${available ? "" : "is-disabled"}"
                data-try-on-date="${value}"
                ${available ? "" : "disabled"}
              >
                ${date.getDate()}
              </button>
            `;
          }).join("")}
        </div>

        <div class="dr-try-on__slots-wrap">
          <h3>${escapeHtml(formatLongDate(state.selectedDate))}</h3>
          <div class="dr-try-on__slots">
            ${selectedSlots.length > 0 ? selectedSlots.map((slot) => `
              <button
                type="button"
                class="dr-try-on__slot ${state.selectedTime === slot.value ? "is-selected" : ""} ${slot.available ? "" : "is-disabled"}"
                data-try-on-time="${escapeHtml(slot.value)}"
                ${slot.available ? "" : "disabled"}
              >
                <span>${escapeHtml(formatTimeRange(slot.value, slot.endTime))}</span>
                <small>${slot.available ? `${slot.spotsLeft} spot${slot.spotsLeft === 1 ? "" : "s"} left` : escapeHtml(slot.unavailableReason || "Full")}</small>
              </button>
            `).join("") : '<p class="dr-try-on__empty">No times available for this date.</p>'}
          </div>
        </div>

        <form class="dr-try-on__form" data-try-on-form>
          <input type="hidden" name="date" value="${escapeHtml(state.selectedDate)}">
          <input type="hidden" name="startTime" value="${escapeHtml(state.selectedTime)}">

          <label>
            <span>Name *</span>
            <input name="customerName" required autocomplete="name">
          </label>

          <label>
            <span>Email *</span>
            <input name="customerEmail" type="email" required autocomplete="email">
          </label>

          <label>
            <span>Phone *</span>
            <input name="customerPhone" required autocomplete="tel">
          </label>

          <label>
            <span>Instagram handle</span>
            <input name="instagramHandle" placeholder="@username">
          </label>

          <label class="dr-try-on__wide">
            <span>Notes</span>
            <textarea name="note" rows="3" placeholder="Tell us your preferred size, event date, or anything helpful."></textarea>
          </label>

          <button type="submit" class="dr-try-on__submit" ${state.selectedTime ? "" : "disabled"}>${escapeHtml(buttonLabel)}</button>
          <div class="dr-try-on__message" data-try-on-message></div>
        </form>
      </div>
    `;

    bindEvents(root, state);
  }

  function bindEvents(root, state) {
    const prev = root.querySelector("[data-try-on-prev]");
    const next = root.querySelector("[data-try-on-next]");
    const form = root.querySelector("[data-try-on-form]");

    prev?.addEventListener("click", () => {
      state.monthDate = new Date(state.monthDate.getFullYear(), state.monthDate.getMonth() - 1, 1);
      render(root, state);
    });

    next?.addEventListener("click", () => {
      state.monthDate = new Date(state.monthDate.getFullYear(), state.monthDate.getMonth() + 1, 1);
      render(root, state);
    });

    root.querySelectorAll("[data-try-on-date]").forEach((button) => {
      button.addEventListener("click", () => {
        state.selectedDate = button.dataset.tryOnDate;
        state.selectedTime = "";
        render(root, state);
      });
    });

    root.querySelectorAll("[data-try-on-time]").forEach((button) => {
      button.addEventListener("click", () => {
        state.selectedTime = button.dataset.tryOnTime;
        render(root, state);
      });
    });

    form?.addEventListener("submit", async (event) => {
      event.preventDefault();
      await submitBooking(root, state, form);
    });
  }

  async function submitBooking(root, state, form) {
    const message = root.querySelector("[data-try-on-message]");
    const submitButton = form.querySelector("button[type='submit']");
    const formData = new FormData(form);

    const payload = {
      shop: root.dataset.shop || "",
      productId: root.dataset.productId || "",
      productGid: root.dataset.productGid || "",
      variantId: getSelectedVariantId(root),
      variantGid: getSelectedVariantGid(root),
      productTitle: root.dataset.productTitle || state.data.product?.title || "",
      date: state.selectedDate,
      startTime: state.selectedTime,
      customerName: formData.get("customerName") || "",
      customerEmail: formData.get("customerEmail") || "",
      customerPhone: formData.get("customerPhone") || "",
      instagramHandle: formData.get("instagramHandle") || "",
      note: formData.get("note") || "",
    };

    message.textContent = "";
    message.className = "dr-try-on__message";
    submitButton.disabled = true;
    submitButton.textContent = "Booking...";

    try {
      const response = await fetch(root.dataset.apiUrl || "/apps/dress-rental/try-ons", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "This try-on could not be booked.");
      }

      message.classList.add("is-success");
      message.textContent = result.message || "Try-on booked successfully.";

      await addTryOnProductToCart(root, result.orderLineItem);
    } catch (error) {
      message.classList.add("is-error");
      message.textContent = error.message || "This try-on could not be booked.";
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = root.dataset.buttonLabel || "Book try-on";
    }
  }

  async function refreshAvailability(root, state) {
    const response = await fetch(buildUrl(root), {
      headers: {
        Accept: "application/json",
      },
    });
    const data = await response.json();

    if (data?.showWidget) {
      state.data = data;
      state.availableDateSet = new Set(data.availableDates || []);
      state.selectedTime = "";
      render(root, state);
    }
  }

  async function init(root) {
    if (root.dataset.tryOnLoaded === "true") return;
    root.dataset.tryOnLoaded = "true";

    try {
      const response = await fetch(buildUrl(root), {
        headers: {
          Accept: "application/json",
        },
      });
      const data = await response.json();

      if (!data?.showWidget) {
        root.remove();
        return;
      }

      const state = createState(data);
      render(root, state);
    } catch {
      root.innerHTML = '<div class="dr-try-on__error">Try-on bookings are unavailable right now.</div>';
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
